import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ExternalLink, Globe2, ListTree, FileText, LogOut, Hotel, Images } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { media } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("ihd_token")) return setUser(false);
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const login = (token, u) => { localStorage.setItem("ihd_token", token); setUser(u); };
  const logout = () => { localStorage.removeItem("ihd_token"); setUser(false); };
  return <AuthCtx.Provider value={{ user, login, logout }}>{children}</AuthCtx.Provider>;
};

const navCls = ({ isActive }) => `flex items-center gap-1.5 text-sm px-2 py-1.5 rounded transition-colors ${isActive ? "text-crimson" : "text-stone-600 hover:text-charcoal"}`;

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { content } = useSite();
  const nav = useNavigate();
  if (user === null) return <div className="admin-root p-10 text-stone-400" data-testid="admin-loading">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return (
    <div className="admin-root min-h-screen bg-[#F8F7F4] text-charcoal">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            {content && <img src={media(content.site.logo)} alt="IHD" className="h-9 w-auto" />}
            <span className="text-stone-400 text-sm border-l border-stone-200 pl-4 hidden sm:inline">Admin Portal</span>
          </div>
          <nav className="flex flex-wrap items-center gap-1 sm:gap-3">
            <a href="/" target="_blank" rel="noreferrer" className={navCls({})} data-testid="admin-nav-view-site">View Site<ExternalLink className="w-3.5 h-3.5" /></a>
            <NavLink to="/admin" end className={navCls} data-testid="admin-nav-listings"><Hotel className="w-4 h-4" />Listings</NavLink>
            <NavLink to="/admin/cities" className={navCls} data-testid="admin-nav-cities"><Globe2 className="w-4 h-4" />Cities</NavLink>
            <NavLink to="/admin/menu" className={navCls} data-testid="admin-nav-menu"><ListTree className="w-4 h-4" />Menu</NavLink>
            <NavLink to="/admin/content" className={navCls} data-testid="admin-nav-content"><FileText className="w-4 h-4" />Website Content</NavLink>
            <NavLink to="/admin/media" className={navCls} data-testid="admin-nav-media"><Images className="w-4 h-4" />Media Library</NavLink>
            <button onClick={() => { logout(); nav("/admin/login"); }} className={navCls({})} data-testid="admin-logout-btn"><LogOut className="w-4 h-4" />Logout</button>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
};
